import React, {useEffect, useState} from 'react';
import InputLabel from "../../components/Input/InputLabel/InputLabel";
import "./StocksChat.scss";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import Button from "../../components/Buttons/Button/Button";
import {RequestCommunity} from "../../request/RequestCommunity";
import utils from "../../utils/utils.json";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {Avatar} from "@mui/material";
import {Auth} from "../../utils/Auth";
import {useTranslation} from "react-i18next";
import {useNotification} from "../NotificationContext/NotificationContext";

const StocksChat = ({stocks}) => {
  const auth = new Auth();
  const [commentaires, setCommentaires] = useState([]);
  const [addCommentaire, setAddCommentaire] = useState("");
  const [isEdit, setIsEdit] = useState(null);
  const [editMessage, setEditMessage] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const {t} = useTranslation();
  const requestCommunity = new RequestCommunity();
  const username = auth.getUsername()
  const notificationService = useNotification();

  useEffect(() => {
    getAllComment();
  }, [])

  async function getAllComment() {
    const response = await requestCommunity.getAllComment(stocks);
    response.data.sort((a, b) => new Date(b.id) - new Date(a.id))
    setCommentaires(response.data);
  }

  async function addComment() {
    if (addCommentaire.trim().length > 0) {
      if (isUpdating) return;
      setIsUpdating(true);
      const tempId = Date.now().toString();
      const newCommentText = addCommentaire;
      const prevComments = [...commentaires];
      
      const optimisticComment = {
          id: tempId,
          _tempKey: tempId,
          content: newCommentText,
          userId: username,
          date: new Date().toISOString(),
          interaction: 0,
          nbInteraction: 0
      };
      
      setCommentaires([optimisticComment, ...commentaires]);
      setAddCommentaire("");

      try {
          const resp = await requestCommunity.addComment(newCommentText, stocks);
          const finalComment = { ...resp.data, _tempKey: tempId };
          setCommentaires(current => current.map(c => c.id === tempId ? finalComment : c));
      } catch (e) {
          setCommentaires(prevComments);
          notificationService.notify(t('error_transaction'), 'error');
      } finally {
          setIsUpdating(false);
      }
    }
  }

  async function likeAction(id) {
    if (auth.isLoggedIn()) {
      const index = commentaires.findIndex(objet => objet.id === id);
      if (index !== -1) {
        const item = commentaires[index];
        const prevInteraction = item.interaction;
        const prevNb = item.nbInteraction;
        
        const nouveauxObjets = [...commentaires];
        nouveauxObjets[index] = {
            ...item, 
            interaction: prevInteraction ? 0 : 1,
            nbInteraction: prevInteraction ? prevNb - 1 : prevNb + 1
        };
        setCommentaires(nouveauxObjets);

        try {
            const resp = await requestCommunity.likeComment(id, utils.interaction.LIKE);
            const actualisesObjets = [...nouveauxObjets];
            actualisesObjets[index] = { ...resp.data, _tempKey: item._tempKey };
            setCommentaires(actualisesObjets);
        } catch (e) {
            const revertedObjets = [...commentaires];
            revertedObjets[index] = { ...item, interaction: prevInteraction, nbInteraction: prevNb };
            setCommentaires(revertedObjets);
        }
      }
    } else {
      notificationService.notify(t('stock.chat.needLogin'), 'info');
    }
  }

  async function deleteAction(id) {
    setDeletingId(id);
    setTimeout(async () => {
        const prevComments = [...commentaires];
        setCommentaires(commentaires => commentaires.filter(objet => objet.id !== id));
        try {
            await requestCommunity.deleteComment(id);
        } catch (e) {
            setCommentaires(prevComments);
        }
        setDeletingId(null);
    }, 300);
  }

  async function initEditMessage(id, content) {
    await setIsEdit(id);
    await setEditMessage(content);
  }

  function stopEdit() {
    setIsEdit(null);
    setEditMessage("");
  }

  async function validateEdit() {
    if (isUpdating) return;
    setIsUpdating(true);
    
    const index = commentaires.findIndex(objet => objet.id === isEdit);
    const item = commentaires[index];
    const prevContent = item.content;
    const currentEditId = isEdit;

    const nouveauxObjets = [...commentaires];
    nouveauxObjets[index] = { ...item, content: editMessage };
    setCommentaires(nouveauxObjets);
    setIsEdit(null);
    setEditMessage("");

    setHighlightId(currentEditId);
    setTimeout(() => setHighlightId(null), 150);

    try {
      const resp = await requestCommunity.editComment(currentEditId, editMessage);
      setCommentaires(current => {
         const newArray = [...current];
         const idx = newArray.findIndex(o => o.id === currentEditId);
         if(idx !== -1) newArray[idx] = { ...resp.data, _tempKey: item._tempKey };
         return newArray;
      });
    } catch (e) {
      setCommentaires(current => {
         const newArray = [...current];
         const idx = newArray.findIndex(o => o.id === currentEditId);
         if(idx !== -1) newArray[idx] = { ...item, content: prevContent };
         return newArray;
      });
    } finally {
      setIsUpdating(false);
    }
  }

  function stringToColor(string) {
    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  }

  return (
    <div className="w-100">
      <h2 className="mb-4">{t('stock.chat.commentCount', {count: commentaires.length})}</h2>

      {auth.isLoggedIn() ? (
        <div className="d-flex w-100 align-center comment-input-container">
          <Avatar sx={{bgcolor: stringToColor(username), width: 40, height: 40}}>{username[0].toUpperCase()}</Avatar>
          <div className="input-wrapper w-100 ml-3 position-relative">
             <input type="text" placeholder={t('stock.chat.addComment')} value={addCommentaire} onChange={(e) => setAddCommentaire(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addComment()} className="comment-input" />
             <div className="send-btn" onClick={addComment}>
                <SendIcon style={{ color: addCommentaire ? '#2563eb' : '#94a3b8' }} />
             </div>
          </div>
        </div>
      ) : (
        <div className="logged-out-state mb-4 mt-2 text-center d-flex flex-column align-center justify-center p-4" style={{ backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
            <LockOutlinedIcon sx={{ fontSize: 28, color: '#94a3b8', marginBottom: '8px' }} />
            <div className="text-gray mb-3 font-bold" style={{ fontSize: '1rem' }}>{t('stock.chat.needLogin')}</div>
        </div>
      )}

      <div className="d-flex flex-column w-100 mt-4">
          {
            commentaires.map((item, index) => (
              <div className={`comment-card w-100 mb-4 ${deletingId === item.id ? 'deleting' : ''} ${highlightId === item.id ? 'highlight' : ''}`} key={item._tempKey || item.id}>
                <div className="d-flex w-100 align-start">
                  <Avatar sx={{bgcolor: stringToColor(item.userId), width: 44, height: 44, fontWeight: 'bold'}}>{item.userId[0].toUpperCase()}</Avatar>
                  
                  <div className="ml-3 w-100" style={{minWidth: 0}}>
                    <div className="d-flex w-100 justify-between align-start mb-2">
                      <div className="d-flex align-baseline flex-wrap">
                         <p className="comment-author">{item.userId}</p>
                         <span style={{color: '#cbd5e1', fontSize: '0.85rem', margin: '0 8px'}}>•</span>
                         <p className="comment-date">{new Date(item.date).toLocaleDateString()}</p>
                      </div>
                      
                      {username === item.userId && (
                        <div className="comment-actions ml-2">
                           <EditIcon className="action-icon" onClick={() => initEditMessage(item.id, item.content)} />
                           <DeleteIcon className="action-icon delete" onClick={() => deleteAction(item.id)} />
                        </div>
                      )}
                    </div>
                    
                    {
                      isEdit === item.id ? (
                        <div className="edit-container mt-1 mb-2">
                          <input type="text" className="comment-input edit-mode w-100" value={editMessage}
                                 onChange={(e) => setEditMessage(e.target.value)}
                                 onKeyDown={(e) => {
                                     if (e.key === 'Enter') validateEdit();
                                     if (e.key === 'Escape') stopEdit();
                                 }}
                          />
                          <div className="d-flex mt-2 justify-end">
                              <Button children={t('stock.chat.save')} styles={"button black py-2 px-3"} handleClick={validateEdit}/>
                              <Button children={t('stock.chat.cancel')} styles={"button white border py-2 px-3 ml-2"} handleClick={stopEdit}/>
                          </div>
                        </div>
                      ) : (
                        <p className="comment-text">{item.content}</p>
                      )
                    }
                    
                    <div className="d-flex align-center mt-2">
                       <div className={`like-btn ${item.interaction ? 'liked' : ''}`} onClick={() => likeAction(item.id)}>
                          {item.interaction ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                          <span className="ml-2">{item.nbInteraction}</span>
                       </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          }
          {commentaires.length === 0 && (
            <p className="text-gray text-center mt-4">{t('stock.chat.noComment')}</p>
          )}

      </div>
    </div>
  );
};

export default StocksChat;